// Evalúa la completitud de una propuesta V2 para decidir si puede enviarse al cliente.
// Twin de taxticbackend/src/utils/propuestaV2Completitud.js — cualquier cambio en
// reglas, catálogos o shape del output debe replicarse en ambos archivos.

import { FRECUENCIA_PLAN_LABEL } from "./propuestaItemLabels";

const CAMPOS_CRITICOS_CABECERA = [
  {
    campo: "solicitanteId",
    label: "Solicitante",
    seccion: "Portada",
    anchor: "seccion-solicitante",
  },
  {
    campo: "titulo",
    label: "Título de la propuesta",
    seccion: "Portada",
    anchor: "seccion-titulo",
  },
];

const CAMPOS_CRITICOS_ITEM_SIEMPRE = [
  { campo: "refCliente", label: "Cliente asignado", anchor: "refCliente" },
  {
    campo: "refEmpleado",
    label: "Responsable asignado",
    anchor: "refEmpleado",
  },
];

const CAMPOS_CRITICOS_ITEM_PLAN_SIEMPRE = [
  {
    campo: "refTipoPlan",
    label: "Servicio del catálogo",
    anchor: "refTipoPlan",
  },
  {
    campo: "tipoFrecuencia",
    label: "Tipo de frecuencia (mensual/anual)",
    anchor: "tipoFrecuencia",
  },
  {
    campo: "fechaSiguienteCiclo",
    label: "Fecha de inicio del ciclo",
    anchor: "fechaSiguienteCiclo",
  },
];

const CAMPOS_CRITICOS_ITEM_PLAN_ANUAL = [
  {
    campo: "mesCargaTareas",
    label: "Mes de carga de tareas",
    anchor: "mesCargaTareas",
  },
  {
    campo: "mesFacturacion",
    label: "Mes de facturación",
    anchor: "mesFacturacion",
  },
];

const CAMPOS_RECOMENDADOS_CABECERA = [
  {
    campo: "descripcion",
    label: "Descripción breve",
    seccion: "Portada",
    anchor: "seccion-descripcion",
  },
  {
    campo: "antecedentes",
    label: "Antecedentes del cliente",
    seccion: "Antecedentes",
    anchor: "seccion-antecedentes",
  },
  {
    campo: "alcance",
    label: "Alcance del servicio",
    seccion: "Estructura",
    anchor: "seccion-alcance",
  },
  {
    campo: "resumenEjecutivo",
    label: "Resumen ejecutivo",
    seccion: "Quiénes Somos",
    anchor: "seccion-resumenEjecutivo",
  },
  {
    campo: "proximosPasos",
    label: "Próximos pasos",
    seccion: "Info. adicional",
    anchor: "seccion-proximosPasos",
  },
  {
    campo: "condicionesPago",
    label: "Condiciones de pago",
    seccion: "Info. adicional",
    anchor: "seccion-condicionesPago",
  },
  {
    campo: "duracionEstimada",
    label: "Duración estimada",
    seccion: "Info. adicional",
    anchor: "seccion-duracionEstimada",
  },
];

function esStringVacio(v) {
  if (v == null) return true;
  if (typeof v !== "string") return true;
  return v.trim() === "";
}

function esEnteroValido(v) {
  if (v == null) return false;
  const n = Number(v);
  return Number.isInteger(n) && n > 0;
}

function esFechaISOValida(v) {
  if (typeof v !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(v);
  return !isNaN(d.getTime());
}

function esMesValido(v) {
  return typeof v === "string" && /^(0[1-9]|1[0-2])$/.test(v);
}

function descripcionItem(item) {
  const tipo = item.tipoItem || "ITEM";
  const nombre =
    item.nombreServicio ||
    item.refTipoPlanLabel ||
    item.refTipoPlan ||
    "sin nombre";
  const cliente = item.refCliente || "sin cliente";
  return `${tipo} ${nombre} - ${cliente}`;
}

/**
 * Evalúa qué tan completa está una propuesta V2 para ser enviada al cliente.
 * Función pura: no muta ni dispara side effects.
 *
 * @param {Object|null|undefined} propuesta
 *   Shape igual al que devuelve GET /propuestas/v2/:id (camelCase, items y equipo embebidos).
 * @returns {{ critical: Array<Object>, recommended: Array<Object> }}
 *   Cada entrada: { campo, label, seccion, anchor, contexto }.
 */
export function evaluarCompletitudPropuestaV2(propuesta) {
  const critical = [];
  const recommended = [];

  if (!propuesta || typeof propuesta !== "object") {
    CAMPOS_CRITICOS_CABECERA.forEach((c) =>
      critical.push({ ...c, contexto: null }),
    );
    critical.push({
      campo: "items",
      label: "Al menos un servicio",
      seccion: "Servicios",
      anchor: "seccion-servicios",
      contexto: null,
    });
    critical.push({
      campo: "equipo",
      label: "Al menos un miembro del equipo",
      seccion: "Equipo",
      anchor: "seccion-equipo",
      contexto: null,
    });
    return { critical, recommended };
  }

  CAMPOS_CRITICOS_CABECERA.forEach((c) => {
    const vacio =
      c.campo === "solicitanteId"
        ? !esEnteroValido(propuesta.solicitanteId ?? propuesta.solicitante?.id)
        : esStringVacio(propuesta[c.campo]);
    if (vacio) critical.push({ ...c, contexto: null });
  });

  CAMPOS_RECOMENDADOS_CABECERA.forEach((c) => {
    if (esStringVacio(propuesta[c.campo]))
      recommended.push({ ...c, contexto: null });
  });

  const items = Array.isArray(propuesta.items) ? propuesta.items : [];
  if (items.length === 0) {
    critical.push({
      campo: "items",
      label: "Al menos un servicio",
      seccion: "Servicios",
      anchor: "seccion-servicios",
      contexto: null,
    });
  }

  items.forEach((item, index) => {
    const seccionItem = `Servicios - ítem ${index + 1}`;
    const contexto = {
      itemIndex: index,
      descripcionItem: descripcionItem(item),
    };

    CAMPOS_CRITICOS_ITEM_SIEMPRE.forEach((c) => {
      if (esStringVacio(item[c.campo])) {
        critical.push({
          campo: `items[${index}].${c.campo}`,
          label: c.label,
          seccion: seccionItem,
          anchor: `item-${index}-${c.anchor}`,
          contexto,
        });
      }
    });

    if (item.tipoItem === "PLAN") {
      CAMPOS_CRITICOS_ITEM_PLAN_SIEMPRE.forEach((c) => {
        let vacio;
        if (c.campo === "refTipoPlan") vacio = esStringVacio(item.refTipoPlan);
        else if (c.campo === "tipoFrecuencia")
          vacio = !Object.keys(FRECUENCIA_PLAN_LABEL).includes(
            item.tipoFrecuencia,
          );
        else if (c.campo === "fechaSiguienteCiclo")
          vacio = !esFechaISOValida(item.fechaSiguienteCiclo);
        else vacio = esStringVacio(item[c.campo]);
        if (vacio) {
          critical.push({
            campo: `items[${index}].${c.campo}`,
            label: c.label,
            seccion: seccionItem,
            anchor: `item-${index}-${c.anchor}`,
            contexto,
          });
        }
      });

      if (item.tipoFrecuencia === "AÑO") {
        CAMPOS_CRITICOS_ITEM_PLAN_ANUAL.forEach((c) => {
          if (!esMesValido(item[c.campo])) {
            critical.push({
              campo: `items[${index}].${c.campo}`,
              label: c.label,
              seccion: seccionItem,
              anchor: `item-${index}-${c.anchor}`,
              contexto,
            });
          }
        });
      }
    } else if (item.tipoItem !== "TAREA") {
      critical.push({
        campo: `items[${index}].tipoItem`,
        label: "Tipo de servicio inválido",
        seccion: seccionItem,
        anchor: `item-${index}-tipoItem`,
        contexto,
      });
    }
  });

  const equipo = Array.isArray(propuesta.equipo) ? propuesta.equipo : [];
  if (equipo.length === 0) {
    critical.push({
      campo: "equipo",
      label: "Al menos un miembro del equipo",
      seccion: "Equipo",
      anchor: "seccion-equipo",
      contexto: null,
    });
  }

  return { critical, recommended };
}

export const CONSTANTES = {
  CAMPOS_CRITICOS_CABECERA,
  CAMPOS_CRITICOS_ITEM_SIEMPRE,
  CAMPOS_CRITICOS_ITEM_PLAN_SIEMPRE,
  CAMPOS_CRITICOS_ITEM_PLAN_ANUAL,
  CAMPOS_RECOMENDADOS_CABECERA,
};
